/**
 * useEingehendeAnrufe - laedt eingehende fonio-Anrufe und triggert das Live-Popup
 * inkl. auffaelliger Benachrichtigung (Gong, Desktop-Notification, Vibration).
 *
 * Trigger: Supabase-Realtime (sofort) + Polling alle 10s (Fallback).
 * Bereits beim Laden vorhandene Anrufe loesen KEIN Popup aus.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { callerDisplay, formatGermanPhone, isBekannt } from '@/lib/fonioAnruf';
import type { EingehenderAnruf } from '@/lib/fonioAnruf';

const TABLE = 'eingehende_anrufe';
const POLL_MS = 10000;

export interface TerminInput {
  titel: string;
  beginnLocal: string;
  dauerMin?: number;
  endeLocal?: string;
  ort?: string;
  beschreibung?: string;
  mitarbeiterId?: string | null;
}

// ---- Audio (Gong) ----
let audioCtx: AudioContext | null = null;
function ensureCtx(): AudioContext | null {
  try {
    const Ctx =
      (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
    return audioCtx;
  } catch {
    return null;
  }
}

function gongOnce(ctx: AudioContext, at: number) {
  const freqs = [196, 392, 587];
  freqs.forEach((f, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = f;
    const peak = 0.6 / (i + 1);
    const t0 = ctx.currentTime + at;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.8);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 1.9);
  });
}

function playGong() {
  const ctx = ensureCtx();
  if (!ctx) return;
  gongOnce(ctx, 0);
  gongOnce(ctx, 0.9);
}

export function unlockAudio() {
  ensureCtx();
}

export function requestNotificationPermission() {
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  } catch {
    /* ignore */
  }
}

function notify(call: EingehenderAnruf) {
  const lead = !isBekannt(call);
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      const n = new Notification(lead ? 'Neuer Lead - Anruf!' : 'Neuer Anruf', {
        body: `${callerDisplay(call)} - ${formatGermanPhone(call.from_number) || 'Nummer unbekannt'}`,
        tag: 'fonio-anruf-' + call.id,
        requireInteraction: true,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    }
  } catch {
    /* ignore */
  }
  try {
    navigator.vibrate?.([300, 120, 300, 120, 300]);
  } catch {
    /* ignore */
  }
}

export function useEingehendeAnrufe(limit = 30) {
  const [calls, setCalls] = useState<EingehenderAnruf[]>([]);
  const [popupCall, setPopupCall] = useState<EingehenderAnruf | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const seenIds = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  const fetchCalls = useCallback(async (): Promise<EingehenderAnruf[]> => {
    const { data } = await supabase
      .from(TABLE)
      .select('*')
      .order('eingegangen_am', { ascending: false })
      .limit(limit);
    return (data as EingehenderAnruf[]) ?? [];
  }, [limit]);

  const handleIncoming = useCallback((c: EingehenderAnruf) => {
    setPopupCall(c);
    playGong();
    notify(c);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchCalls();
        if (!active) return;
        data.forEach((c) => seenIds.current.add(c.id));
        setCalls(data);
        initialized.current = true;
      } catch {
        /* ignore */
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchCalls]);

  useEffect(() => {
    const channel = supabase
      .channel('eingehende-anrufe-header')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: TABLE }, (payload) => {
        const c = payload.new as EingehenderAnruf;
        if (seenIds.current.has(c.id)) return;
        seenIds.current.add(c.id);
        setCalls((prev) => [c, ...prev].slice(0, limit));
        handleIncoming(c);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: TABLE }, (payload) => {
        const c = payload.new as EingehenderAnruf;
        setCalls((prev) => prev.map((p) => (p.id === c.id ? c : p)));
        setPopupCall((prev) => (prev && prev.id === c.id ? c : prev));
      })
      .subscribe();
    channelRef.current = channel;
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [limit, handleIncoming]);

  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const data = await fetchCalls();
        if (!initialized.current) return;
        const fresh = data.filter((c) => !seenIds.current.has(c.id));
        data.forEach((c) => seenIds.current.add(c.id));
        setCalls(data);
        if (fresh.length > 0) handleIncoming(fresh[0]);
      } catch {
        /* ignore */
      }
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [fetchCalls, handleIncoming]);

  const setStatus = useCallback(async (id: string, status: 'gesehen' | 'erledigt') => {
    setCalls((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    try {
      await supabase.from(TABLE).update({ status }).eq('id', id);
    } catch {
      /* ignore */
    }
  }, []);

  const markAlleGesehen = useCallback(async () => {
    const offen = calls.filter((c) => c.status === 'eingegangen').map((c) => c.id);
    if (offen.length === 0) return;
    setCalls((prev) => prev.map((c) => (c.status === 'eingegangen' ? { ...c, status: 'gesehen' } : c)));
    try {
      await supabase.from(TABLE).update({ status: 'gesehen' }).in('id', offen);
    } catch {
      /* ignore */
    }
  }, [calls]);

  const dismissPopup = useCallback(() => setPopupCall(null), []);

  // Termin in den App-Kalender (Tabelle termine) anlegen - umfangreich
  const terminAnlegen = useCallback(async (call: EingehenderAnruf, t: TerminInput): Promise<boolean> => {
    const beginn = new Date(t.beginnLocal);
    if (isNaN(beginn.getTime())) return false;
    const ende = t.endeLocal ? new Date(t.endeLocal) : new Date(beginn.getTime() + (t.dauerMin ?? 30) * 60000);
    try {
      const { error } = await supabase.from('termine').insert({
        titel: t.titel || 'Telefon-Termin',
        beschreibung: t.beschreibung || call.anliegen || call.zusammenfassung || null,
        ort: t.ort || null,
        beginn: beginn.toISOString(),
        ende: ende.toISOString(),
        kunde_id: call.kunde_id ?? null,
        akte_id: call.akte_id ?? null,
        mitarbeiter_id: t.mitarbeiterId ?? null,
        business_unit: 'telefon',
        erinnerung_minuten: 30,
      });
      return !error;
    } catch {
      return false;
    }
  }, []);

  return { calls, popupCall, setStatus, markAlleGesehen, dismissPopup, terminAnlegen };
}
